import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Table,
} from "react-bootstrap";
import debounce from "lodash/debounce";
import { useReactToPrint } from "react-to-print";
import { InspectionsClient } from "../web-api-client.ts";
import QR from "./QR.js";
import { QRCodeCanvas } from "qrcode.react";
import UserContext from "../providers/UserContext.js";

/**
 * PrintSafeQR
 * - On SCREEN: shows fancy <QR /> preview
 * - On PRINT: shows the print-perfect layout (scanme image + QR + logo)
 */
const PrintSafeQR = ({ url, qrInches = 2.75 }) => {
  const QR_SIZE = 400;
  const MARGIN = 40;
  const EXTRA_RIGHT = 380;

  const ARROW_COLOR = "#C65A43";
  const ARROW_WIDTH = 28;
  const ARROW_HEAD = 44;

  const qrCanvasRef = useRef(null);
  const [pngSrc, setPngSrc] = useState("");

  useEffect(() => {
    let raf = 0;

    const compose = () => {
      const qr = qrCanvasRef.current;
      if (!qr) return;

      const W = MARGIN + QR_SIZE + EXTRA_RIGHT + MARGIN;
      const H = MARGIN + QR_SIZE + MARGIN;

      const comp = document.createElement("canvas");
      comp.width = W;
      comp.height = H;
      const ctx = comp.getContext("2d");

      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, W, H);

      const qx = MARGIN;
      const qy = MARGIN;
      ctx.drawImage(qr, qx, qy);

      const endX = qx + QR_SIZE + 18;
      const endY = qy + QR_SIZE * 0.4;

      const startX = qx + QR_SIZE + EXTRA_RIGHT - 10;
      const startY = qy + QR_SIZE * 0.22;

      const c1x = startX + 20;
      const c1y = startY - 160;
      const c2x = endX + 160;
      const c2y = endY - 60;

      ctx.lineWidth = ARROW_WIDTH;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = ARROW_COLOR;

      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.bezierCurveTo(c1x, c1y, c2x, c2y, endX, endY);
      ctx.stroke();

      const vx = endX - c2x;
      const vy = endY - c2y;
      const vlen = Math.hypot(vx, vy) || 1;
      const ux = vx / vlen;
      const uy = vy / vlen;
      const baseX = endX - ux * (ARROW_HEAD + 8);
      const baseY = endY - uy * (ARROW_HEAD + 8);
      const px = -uy;
      const py = ux;

      const leftX = baseX + px * (ARROW_HEAD * 0.65);
      const leftY = baseY + py * (ARROW_HEAD * 0.65);
      const rightX = baseX - px * (ARROW_HEAD * 0.65);
      const rightY = baseY - py * (ARROW_HEAD * 0.65);

      ctx.fillStyle = ARROW_COLOR;
      ctx.beginPath();
      ctx.moveTo(endX, endY);
      ctx.lineTo(leftX, leftY);
      ctx.lineTo(rightX, rightY);
      ctx.closePath();
      ctx.fill();

      try {
        setPngSrc(comp.toDataURL("image/png"));
      } catch {
        setPngSrc("");
      }
    };

    raf = requestAnimationFrame(compose);
    return () => cancelAnimationFrame(raf);
  }, [url]);

  const COMPOSITE_W = MARGIN + QR_SIZE + EXTRA_RIGHT + MARGIN;
  const qrToCompositeRatio = QR_SIZE / COMPOSITE_W;
  const imgInches = qrInches / qrToCompositeRatio;

  return (
    <>
      <div className="print-only" style={{ textAlign: "center" }}>
        <img
          src="scanme.png"
          alt="scan me with arrow"
          style={{
            width: `7in`,
            maxWidth: "100%",
            height: "auto",
            display: "block",
            margin: "0 auto",
          }}
        />

        <div style={{ marginTop: "-150px" }}>
          <QRCodeCanvas ref={qrCanvasRef} value={url} size={QR_SIZE} />
        </div>

        <img
          src="logo-dark.png"
          alt="qcar logo"
          style={{
            width: `4in`,
            maxWidth: "100%",
            height: "auto",
            display: "block",
            margin: "50px auto",
          }}
        />
      </div>

      <div className="screen-only">
        <QR url={url} />
      </div>

      <div
        style={{ position: "absolute", left: -99999 }}
        className="screen-only"
        aria-hidden="true"
      >
        {pngSrc ? (
          <img
            src={pngSrc}
            alt=""
            style={{ width: `${imgInches}in`, height: "auto" }}
          />
        ) : null}
      </div>
    </>
  );
};

const QrBatchPrint = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [inspections, setInspections] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [error, setError] = useState(null);
  const user = React.useContext(UserContext);

  const inspectionsClient = useMemo(() => new InspectionsClient(), []);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sortColumn, setSortColumn] = useState("inspectiondate");
  const [sortDirection, setSortDirection] = useState("desc");

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mqW = window.matchMedia("(max-width: 768px)");
    const mqP = window.matchMedia("(pointer: coarse)");

    const compute = () => setIsMobile(!!(mqW.matches || mqP.matches));
    compute();

    const onChange = () => compute();

    if (mqW.addEventListener) mqW.addEventListener("change", onChange);
    else mqW.addListener(onChange);

    if (mqP.addEventListener) mqP.addEventListener("change", onChange);
    else mqP.addListener(onChange);

    return () => {
      if (mqW.removeEventListener) mqW.removeEventListener("change", onChange);
      else mqW.removeListener(onChange);

      if (mqP.removeEventListener) mqP.removeEventListener("change", onChange);
      else mqP.removeListener(onChange);
    };
  }, []);

  const loadInspections = useCallback(async () => {
    if (!user?.dealerId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await inspectionsClient.getInspectionList(
        user.dealerId,
        pageNumber,
        pageSize,
        search || undefined,
        sortColumn,
        sortDirection,
        statusFilter === "all" ? undefined : Number(statusFilter)
      );

      const normalized = (result?.items || []).map((x) => ({
        id: x.id,
        vehicleLabel: x.vehicleLabel || "",
        vehicleYear: x.vehicleYear || "",
        vehicleMake: x.vehicleMake || "",
        vehicleModel: x.vehicleModel || "",
        vehicleStockNumber: x.vehicleStockNumber || "",
        vehicleVin: x.vehicleVin || x.vehicleLabel || "",
        status: x.status,
        inspectionDate: x.inspectionDate ? new Date(x.inspectionDate) : null,
        inspectorName: x.inspectorName || "",
      }));

      setInspections(normalized);
      setTotalCount(result?.totalCount ?? normalized.length);
      setTotalPages(result?.totalPages ?? 1);

      if (result?.pageNumber && result.pageNumber !== pageNumber) {
        setPageNumber(result.pageNumber);
      }
    } catch (e) {
      console.error(e);
      setError("Failed to load inspections.");
      setInspections([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    inspectionsClient,
    user?.dealerId,
    pageNumber,
    pageSize,
    search,
    sortColumn,
    sortDirection,
    statusFilter,
  ]);

  useEffect(() => {
    loadInspections();
  }, [loadInspections]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setPageNumber(1);
        setSearch(value);
      }, 300),
    []
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  const selectedIds = useMemo(
    () => new Set(Object.keys(selectedMap).map((x) => Number(x))),
    [selectedMap]
  );

  const toggleOne = (row) => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[row.id]) {
        delete next[row.id];
      } else {
        next[row.id] = row;
      }
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const allSelected =
      inspections.length > 0 && inspections.every((x) => selectedIds.has(x.id));

    setSelectedMap((prev) => {
      const next = { ...prev };

      if (allSelected) {
        inspections.forEach((x) => {
          delete next[x.id];
        });
      } else {
        inspections.forEach((x) => {
          next[x.id] = x;
        });
      }

      return next;
    });
  };

  const clearAll = () => setSelectedMap({});
  const canProceed = selectedIds.size > 0;

  const goBack = () => setStep(1);

  const selectedList = useMemo(() => {
    return Object.values(selectedMap).sort((a, b) => {
      const aDate = a?.inspectionDate ? new Date(a.inspectionDate).getTime() : 0;
      const bDate = b?.inspectionDate ? new Date(b.inspectionDate).getTime() : 0;
      return bDate - aDate;
    });
  }, [selectedMap]);

  const qrUrlFor = (id) => `${window.location.origin}/dvi/scan/${id}`;

  const getSortIndicator = (column) => {
    if (sortColumn !== column) return "";
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  const handleSort = (column) => {
    setPageNumber(1);
    setSortColumn((prevColumn) => {
      if (prevColumn === column) {
        setSortDirection((prevDirection) =>
          prevDirection === "asc" ? "desc" : "asc"
        );
        return prevColumn;
      }

      setSortDirection("asc");
      return column;
    });
  };

  const CRITICAL_PRINT_CSS = `
    @page { size: Letter portrait; margin: 0.5in; }

    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #fff !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    }
    *, *::before, *::after { box-sizing: border-box; }

    .no-print { display: none !important; }
    .print-page { break-after: page; page-break-after: always; }

    .preview-stack { display: block !important; }
    .page-shadow { all: unset; display: block; }

    .qr-fullpage {
      width: 100% !important;
      min-height: calc(11in - 1in) !important;
      padding: 0 !important;
      display: flex !important;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: #000 !important;
    }

    .qr-box { width: 100% !important; display: grid !important; place-items: center; }

    .screen-only { display: none !important; }
    .print-only  { display: block !important; }

    .qr-meta { margin-top: 8pt; font-size: 12pt; line-height: 1.25; color: #000 !important; }
    .qr-title { font-weight: 700; font-size: 13pt; color: #000 !important; }
  `;

  const printAreaRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printAreaRef,
    documentTitle: "QCar - Inspection QRs",
    removeAfterPrint: true,
    copyStyles: true,
    pageStyle: CRITICAL_PRINT_CSS,
    onBeforeGetContent: () =>
      new Promise((resolve) => requestAnimationFrame(resolve)),
  });

  const [printing, setPrinting] = useState(false);

  const goNext = () => {
    if (!canProceed) return;

    if (isMobile) {
      setPrinting(true);
      requestAnimationFrame(() => {
        try {
          handlePrint?.();
        } finally {
          setTimeout(() => setPrinting(false), 800);
        }
      });
      return;
    }

    setStep(2);
  };

  const pageStart = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const pageEnd = Math.min(pageNumber * pageSize, totalCount);

  return (
    <Container fluid className="py-3">
      <style>{`
        .preview-stack { display: grid; gap: 24px; }
        .page-shadow {
          background: white;
          margin: 0 auto;
          width: 8.5in;
          max-width: 95vw;
          aspect-ratio: 8.5 / 11;
          box-shadow: 0 6px 24px rgba(0,0,0,0.18);
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
        }
        .qr-fullpage {
          width: 100%;
          height: 100%;
          padding: 0.5in;
          display: flex; flex-direction: column;
          align-items: center; justify-content: center;
          text-align: center; color: #000 !important;
        }
        .qr-meta { margin-top: 12px; font-size: 1rem; line-height: 1.2; word-break: break-word; color: #000 !important; }
        .qr-title { font-weight: 700; font-size: 1.1rem; color: #000 !important; }

        .desktop-print-preview .screen-only { display: none !important; }
        .desktop-print-preview .print-only  { display: block !important; }

        .desktop-print-preview .qr-fullpage {
          justify-content: flex-start !important;
          padding-top: 0.75in !important;
        }

        .sortable-header {
          cursor: pointer;
          white-space: nowrap;
        }
      `}</style>

      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          width: 0,
          height: 0,
          overflow: "hidden",
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        <div ref={printAreaRef}>
          <style media="print">{CRITICAL_PRINT_CSS}</style>

          <div className="preview-stack">
            {selectedList.map((item) => {
              const url = qrUrlFor(item.id);
              return (
                <div className="page-shadow print-page" key={item.id}>
                  <div className="qr-fullpage">
                    <div className="qr-box">
                      <PrintSafeQR url={url} />
                    </div>
                    <div className="qr-meta">
                      <div className="qr-title">
                        {item.vehicleLabel || `Inspection #${item.id}`}
                      </div>
                      <div>{item.vehicleVin || "—"}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {step === 1 && (
        <>
          <Row className="mb-3">
            <Col>
              <h3 className="mb-0">Print QR Codes – Select Inspections</h3>
            </Col>
          </Row>

          <Card className="mb-3">
            <Card.Body>
              <Row className="g-2 align-items-end">
                <Col md={4}>
                  <Form.Label className="mb-1">Search</Form.Label>
                  <InputGroup>
                    <Form.Control
                      placeholder="VIN, Make Model, Stock #..."
                      value={searchInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchInput(value);
                        debouncedSearch(value);
                      }}
                    />
                    {searchInput && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setSearchInput("");
                          debouncedSearch.cancel();
                          setPageNumber(1);
                          setSearch("");
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </Col>

                <Col md={2}>
                  <Form.Label className="mb-1">Status</Form.Label>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => {
                      setPageNumber(1);
                      setStatusFilter(e.target.value);
                    }}
                  >
                    <option value="all">All</option>
                    <option value="0">Pass</option>
                    <option value="1">Attention</option>
                    <option value="2">Urgent</option>
                    <option value="3">N/A</option>
                  </Form.Select>
                </Col>

                <Col md={2}>
                  <Form.Label className="mb-1">Page Size</Form.Label>
                  <Form.Select
                    value={pageSize}
                    onChange={(e) => {
                      setPageNumber(1);
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </Col>

                <Col md="auto" className="ms-auto">
                  <div className="text-end">
                    <div className="small text-muted">
                      Selected: {selectedIds.size}
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="mt-2"
                      onClick={clearAll}
                      disabled={selectedIds.size === 0}
                    >
                      Clear Selected
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                Inspections
                <span className="text-muted ms-2 small">
                  {totalCount > 0
                    ? `Showing ${pageStart}-${pageEnd} of ${totalCount}`
                    : "0 results"}
                </span>
              </div>
              <div className="no-print">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={toggleAllOnPage}
                  disabled={inspections.length === 0}
                >
                  {inspections.length > 0 &&
                  inspections.every((x) => selectedIds.has(x.id))
                    ? "Unselect Page"
                    : "Select Page"}
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="p-0">
              {loading && (
                <div className="p-3 text-muted">Loading inspections…</div>
              )}
              {error && <div className="p-3 text-danger">{error}</div>}
              {!loading && !error && inspections.length === 0 && (
                <div className="p-3 text-muted">No inspections found.</div>
              )}

              {!loading && !error && inspections.length > 0 && (
                <div className="table-responsive">
                  <Table className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 48 }}></th>
                        <th
                          className="sortable-header"
                          onClick={() => handleSort("vehicleyear")}
                        >
                          Year{getSortIndicator("vehicleyear")}
                        </th>
                        <th
                          className="sortable-header"
                          onClick={() => handleSort("vehiclemake")}
                        >
                          Make{getSortIndicator("vehiclemake")}
                        </th>
                        <th
                          className="sortable-header"
                          onClick={() => handleSort("vehiclemodel")}
                        >
                          Model{getSortIndicator("vehiclemodel")}
                        </th>
                        <th
                          className="sortable-header"
                          onClick={() => handleSort("vehiclestocknumber")}
                        >
                          Stock Number{getSortIndicator("vehiclestocknumber")}
                        </th>
                        <th>Vin</th>
                        <th
                          className="sortable-header"
                          onClick={() => handleSort("inspectiondate")}
                        >
                          Date{getSortIndicator("inspectiondate")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {inspections.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selectedIds.has(row.id)}
                              onChange={() => toggleOne(row)}
                            />
                          </td>
                          <td>{row.vehicleYear || "—"}</td>
                          <td>{row.vehicleMake || "—"}</td>
                          <td>{row.vehicleModel || "—"}</td>
                          <td>{row.vehicleStockNumber || "—"}</td>
                          <td>{row.vehicleVin || "—"}</td>
                          <td>
                            {row.inspectionDate
                              ? row.inspectionDate.toLocaleString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>

            <Card.Footer className="no-print">
              <Row className="g-2 align-items-center">
                <Col md="auto">
                  <div className="small text-muted">
                    Page {pageNumber} of {Math.max(totalPages, 1)}
                  </div>
                </Col>

                <Col className="d-flex justify-content-center gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={pageNumber <= 1 || loading}
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={pageNumber >= totalPages || loading}
                    onClick={() =>
                      setPageNumber((prev) => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next
                  </Button>
                </Col>

                <Col className="d-flex justify-content-end gap-2">
                  <Button
                    variant="primary"
                    disabled={!canProceed || printing}
                    onClick={goNext}
                  >
                    {isMobile
                      ? printing
                        ? "Opening Print…"
                        : "Print Selected"
                      : "Next: Full-Page Preview"}
                  </Button>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </>
      )}

      {step === 2 && (
        <>
          <Row className="no-print mb-3">
            <Col className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">Full-Page QR Preview</h3>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={goBack}>
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handlePrint}
                  disabled={selectedList.length === 0}
                >
                  Print
                </Button>
              </div>
            </Col>
          </Row>

          <div className="preview-stack desktop-print-preview">
            {selectedList.map((item) => {
              const url = qrUrlFor(item.id);
              return (
                <div className="page-shadow" key={item.id}>
                  <div className="qr-fullpage">
                    <div className="qr-box">
                      <PrintSafeQR url={url} />
                    </div>
                    <div className="qr-meta">
                      <div className="qr-title">
                        {item.vehicleLabel || `Inspection #${item.id}`}
                      </div>
                      <div>{item.vehicleVin || "—"}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="no-print d-flex justify-content-end mt-3">
            <Button
              variant="primary"
              onClick={handlePrint}
              disabled={selectedList.length === 0}
            >
              Print
            </Button>
          </div>
        </>
      )}
    </Container>
  );
};

export default QrBatchPrint;